/**
 * Метрики встроенного графика Ads Manager.
 *
 * ★ Лейблы берутся из `columns-config.ts` — того же реестра, что задаёт
 * колонки таблицы. Второй список названий однажды разошёлся бы с первым,
 * и на одном экране «Average CPA (Total)» в колонке соседствовало бы с
 * «CPA total» в легенде графика.
 *
 * ★ Отношения (TTR, CPT, CR, CPA) считаются ЗДЕСЬ, а не на бэке. Так уже
 * устроен `overview_event_series` («Derived CR / ROAS are computed
 * frontend-side»), и второе место, где те же формулы жили бы иначе,
 * разошлось бы с первым при первой же правке.
 *
 * Деление на ноль даёт `null`, а не ноль или бесконечность: день без
 * тапов — это «CPT неизвестен», а не «CPT равен нулю». Линия в такой
 * день обязана прерваться, а не упасть на ось.
 */
import { ALL_COLUMNS, type ColumnKey } from "@/lib/columns-config";
import type { MetricDef } from "@/lib/metrics";
import type { DailyPoint, EntityChartPoint } from "@/lib/types";

/** Столбцы, которые складываются при агрегации. Ровно числовые поля
 *  `EntityChartPoint`, кроме `date` и словарей событий. Список явный,
 *  а не выведенный из читателей: имена метрик и имена столбцов
 *  совпадают не все. */
const BASE_SUM_COLUMNS = [
  "impressions", "taps", "spend_usd", "installs_asa",
  "tap_installs", "view_installs", "total_installs",
  "new_downloads_tap", "new_downloads_view", "new_downloads_total",
  "redownloads_tap", "redownloads_view", "redownloads_total",
] as const satisfies readonly (keyof EntityChartPoint)[];

/** Значения дня по метрике. `null` — величина не определена. */
type Reader = (p: EntityChartPoint) => number | null;

const div = (a: number, b: number, mul = 1): number | null =>
  b > 0 ? (a / b) * mul : null;

/** id → как получить значение дня. Порядок задаёт порядок в дропдауне. */
const READERS: Record<string, Reader> = {
  impressions: (p) => p.impressions,
  taps: (p) => p.taps,
  ttr: (p) => div(p.taps, p.impressions, 100),
  avg_cpt: (p) => div(p.spend_usd, p.taps),
  spend: (p) => p.spend_usd,
  installs_tap: (p) => p.tap_installs,
  installs_view: (p) => p.view_installs,
  installs_total: (p) => p.total_installs,
  cr_tap: (p) => div(p.tap_installs, p.taps, 100),
  cr_total: (p) => div(p.total_installs, p.taps, 100),
  cpa_tap: (p) => div(p.spend_usd, p.tap_installs),
  cpa_view: (p) => div(p.spend_usd, p.view_installs),
  cpa_total: (p) => div(p.spend_usd, p.total_installs),
  new_downloads_tap: (p) => p.new_downloads_tap,
  new_downloads_view: (p) => p.new_downloads_view,
  new_downloads_total: (p) => p.new_downloads_total,
  redownloads_tap: (p) => p.redownloads_tap,
  redownloads_view: (p) => p.redownloads_view,
  redownloads_total: (p) => p.redownloads_total,
};

/** Единица и класс величины. `isRate` разводит оси: объёмные влево,
 *  отношения вправо — существующее правило `ChartCard`. */
const UNITS: Record<string, { unit: string; isRate: boolean }> = {
  ttr: { unit: "%", isRate: true },
  cr_tap: { unit: "%", isRate: true },
  cr_total: { unit: "%", isRate: true },
  avg_cpt: { unit: "$", isRate: true },
  cpa_tap: { unit: "$", isRate: true },
  cpa_view: { unit: "$", isRate: true },
  cpa_total: { unit: "$", isRate: true },
  spend: { unit: "$", isRate: false },
};

// Палитра — из существующих метрик Overview, чтобы график Ads Manager не
// зажил своими цветами рядом с ним.
const COLORS = [
  "var(--c-accent-600)", "#e0833a", "#2563eb", "#1f9d55", "#7c3aed",
  "#ef4444", "#0ea5e9", "#a16207", "#059669", "#64748b",
];

const LABELS: Partial<Record<ColumnKey, string>> = Object.fromEntries(
  ALL_COLUMNS.map((c) => [c.key, c.label]),
) as Partial<Record<ColumnKey, string>>;

function fmt(unit: string): (v: number | null) => string {
  return (v) => {
    if (v == null) return "—";
    if (unit === "$") return v.toFixed(2);
    if (unit === "%") return v.toFixed(2);
    return Math.round(v).toLocaleString();
  };
}

/**
 * Определения метрик для `ChartCard.extraMetrics`.
 *
 * Значения читаются из карты по дате — тот же приём, что у per-app
 * плиток Overview (§36). `ChartCard` принимает `series: DailyPoint[]`, а
 * нам нужны величины, которых в `DailyPoint` нет; замыкание по дате это
 * и решает, не меняя контракт компонента.
 */
/** Итог метрики за весь период.
 *
 * ★ Отношения выводятся из СУММ, а не складываются из дневных — та же
 * арифметика, что и у недельной агрегации, и по той же причине: сумма
 * дневных CPA не равна CPA за период и вообще ничему не равна.
 */
function totalReader(read: Reader, byDate: Map<string, EntityChartPoint>) {
  return (series: { date: string }[]): number | null => {
    const pts = series
      .map((p) => byDate.get(p.date))
      .filter((p): p is EntityChartPoint => p != null);
    if (pts.length === 0) return null;
    return read(sumAll(pts));
  };
}

/** Сложить весь ряд в одну точку. Дата берётся первой — она нужна
 *  только чтобы объект был законной точкой, в расчёт не идёт. */
function sumAll(pts: EntityChartPoint[]): EntityChartPoint {
  const out = { ...pts[0]!, event_counts: {}, event_revenue: {} } as EntityChartPoint;
  for (const c of BASE_SUM_COLUMNS) out[c] = 0;
  for (const p of pts) {
    for (const c of BASE_SUM_COLUMNS) out[c] += p[c] ?? 0;
    for (const [k, v] of Object.entries(p.event_counts)) {
      out.event_counts[k] = (out.event_counts[k] ?? 0) + v;
    }
    for (const [k, v] of Object.entries(p.event_revenue)) {
      out.event_revenue[k] = (out.event_revenue[k] ?? 0) + v;
    }
  }
  return out;
}

export function buildEntityMetrics(
  points: EntityChartPoint[],
  events: string[],
): MetricDef[] {
  const byDate = new Map<string, EntityChartPoint>();
  for (const p of points) byDate.set(p.date, p);

  const base: MetricDef[] = Object.keys(READERS).map((id, i) => {
    const u = UNITS[id] ?? { unit: "", isRate: false };
    const read = READERS[id]!;
    const label = LABELS[id as ColumnKey] ?? id;
    return {
      id,
      label,
      short: label,
      unit: u.unit as MetricDef["unit"],
      color: COLORS[i % COLORS.length]!,
      // У стоимостных «хорошо» — вниз: дешевле значит лучше.
      goodDir: u.unit === "$" && u.isRate ? "down" : "up",
      gated: false,
      isRate: u.isRate,
      fmt: fmt(u.unit),
      fromKpis: () => null,
      fromSeries: (p) => {
        const d = byDate.get(p.date);
        return d ? read(d) : null;
      },
      totalOf: totalReader(read, byDate),
      fromRow: () => null,
      fromByApp: () => null,
    } as MetricDef;
  });

  // События приложения: количество, выручка, CPA и CR по каждому.
  // Revenue показывается только там, где за период она ненулевая — то же
  // правило, что у дропдауна Overview (§53): подметрика, которая всегда
  // пуста, засоряет список и ничего не отвечает.
  const evMetrics: MetricDef[] = [];
  events.forEach((ev) => {
    const color = eventColor(ev);
    const hasRevenue = points.some((p) => (p.event_revenue[ev] ?? 0) > 0);
    const cnt = (p: EntityChartPoint) => p.event_counts[ev] ?? 0;

    evMetrics.push(mk(`event:${ev}:count`, `${ev} — count`, "", false, color,
      byDate, (d) => cnt(d)));
    if (hasRevenue) {
      evMetrics.push(mk(`event:${ev}:revenue`, `${ev} — revenue`, "$", false,
        color, byDate, (d) => d.event_revenue[ev] ?? 0));
    }
    evMetrics.push(mk(`event:${ev}:cpa`, `${ev} — CPA`, "$", true, color,
      byDate, (d) => div(d.spend_usd, cnt(d))));
    evMetrics.push(mk(`event:${ev}:cr`, `${ev} — CR`, "%", true, color,
      byDate, (d) => div(cnt(d), d.total_installs, 100)));
  });

  return [...base, ...evMetrics];
}

function mk(
  id: string, label: string, unit: string, isRate: boolean, color: string,
  byDate: Map<string, EntityChartPoint>, read: Reader,
): MetricDef {
  return {
    id, label, short: label,
    unit: unit as MetricDef["unit"],
    color,
    goodDir: unit === "$" && isRate ? "down" : "up",
    gated: false,
    isRate,
    fmt: fmt(unit),
    fromKpis: () => null,
    fromSeries: (p) => {
      const d = byDate.get(p.date);
      return d ? read(d) : null;
    },
    totalOf: totalReader(read, byDate),
    fromRow: () => null,
    fromByApp: () => null,
  } as MetricDef;
}

/** Дефолт по ТЗ §4.2 — одинаков на всех трёх уровнях. */
export const DEFAULT_CHART_METRICS = ["installs_total", "cpa_total"];

// ---------------------------------------------------------------------------
// Метрика без данных
// ---------------------------------------------------------------------------

/**
 * ★ Цвет события — по ИМЕНИ, а не по месту в списке событий сущности.
 *
 * Список событий у каждой сущности свой: у кампании с одним событием
 * `trial_started` стоит первым, у соседней — третьим. Цвет по позиции
 * менял бы окраску одной и той же метрики при переходе между строками,
 * а метрика, которой у сущности нет вовсе, не имела бы цвета никакого.
 * Хэш имени даёт один цвет везде: в меню, в легенде, на графике и у
 * метрики, по которой за период пусто.
 */
export function eventColor(ev: string): string {
  let h = 5381;
  for (let i = 0; i < ev.length; i++) h = ((h << 5) + h + ev.charCodeAt(i)) | 0;
  return COLORS[Math.abs(h) % COLORS.length]!;
}

const EVENT_KIND: Record<string, { suffix: string; unit: string; isRate: boolean }> = {
  count: { suffix: "count", unit: "", isRate: false },
  revenue: { suffix: "revenue", unit: "$", isRate: false },
  cpa: { suffix: "CPA", unit: "$", isRate: true },
  cr: { suffix: "CR", unit: "%", isRate: true },
};

/** `event:<событие>:<вид>` → его части, иначе `null`. */
export function parseEventMetricId(
  id: string,
): { event: string; kind: keyof typeof EVENT_KIND } | null {
  const m = /^event:(.+):(count|revenue|cpa|cr)$/.exec(id);
  if (!m) return null;
  return { event: m[1]!, kind: m[2] as keyof typeof EVENT_KIND };
}

// Предупреждение — один раз на идентификатор за жизнь страницы: график
// перерисовывается на каждое наведение, и строка на каждый рендер
// превратила бы консоль в шум, в котором первое сообщение и потонет.
const warned = new Set<string>();

/**
 * ★ Метрика, по которой у сущности НЕТ данных, — её собственное имя и
 * «—», а не чужое число.
 *
 * Определения событийных метрик строятся только по событиям, которые у
 * сущности за период были (`buildEntityMetrics`): у кампании без
 * триалов нет и `event:trial_started:count`. Сохранённый выбор при
 * этом такую метрику держит, и прежде `ChartCard` искал её в реестре
 * Overview через `getMetric`, а тот на незнакомый идентификатор отдаёт
 * `METRICS[0]` — Spend. Легенда показывала «Spend $0.00 · Spend $0.00 ·
 * Spend $5.60»: две метрики по событиям исчезали, а Spend стоял трижды.
 *
 * Заглушка знает имя (из идентификатора), единицу, класс величины и
 * цвет; значений у неё нет ни в один день (`fromSeries` → null), ни за
 * период (`totalOf` → null), так что линия не рисуется, а легенда и
 * подсказка пишут «—». Идентификатор, который не разобрать вовсе, —
 * тоже заглушка, под своим идентификатором, с записью в консоль: это
 * ошибка, и она обязана быть видна, а не тихо стать Spend.
 */
export function placeholderMetric(id: string): MetricDef {
  const ev = parseEventMetricId(id);
  const kind = ev ? EVENT_KIND[ev.kind]! : null;
  const label = ev && kind ? `${ev.event} — ${kind.suffix}` : id;
  const unit = kind?.unit ?? "";
  const isRate = kind?.isRate ?? false;
  if (!ev && !warned.has(id)) {
    warned.add(id);
    console.warn(
      `chart metric "${id}" has no definition — shown as "—", not substituted`,
    );
  }
  return {
    id, label, short: label,
    unit: unit as MetricDef["unit"],
    color: ev ? eventColor(ev.event) : COLORS[COLORS.length - 1]!,
    goodDir: unit === "$" && isRate ? "down" : "up",
    gated: false,
    isRate,
    fmt: fmt(unit),
    fromKpis: () => null,
    fromSeries: () => null,
    totalOf: () => null,
    fromRow: () => null,
    fromByApp: () => null,
  } as MetricDef;
}

/**
 * Определения для меню и легенды: построенные по данным плюс заглушка
 * на каждый ВЫБРАННЫЙ идентификатор, которого среди них нет.
 *
 * Без заглушек выбранная метрика без данных пропадала бы и из меню
 * (снять её было бы нечем — счётчик «5/5» при трёх строках), и из
 * легенды. Выбор пользователя при этом не трогается: заглушка — способ
 * его ПОКАЗАТЬ, а не изменить.
 */
export function withSelectedPlaceholders(
  defs: MetricDef[],
  selected: readonly string[],
): MetricDef[] {
  const have = new Set(defs.map((d) => d.id));
  const extra: MetricDef[] = [];
  for (const id of selected) {
    if (have.has(id)) continue;
    have.add(id);
    extra.push(placeholderMetric(id));
  }
  return extra.length === 0 ? defs : [...defs, ...extra];
}

// ---------------------------------------------------------------------------
// Агрегация по дням / неделям / месяцам
// ---------------------------------------------------------------------------

export type ChartPeriod = "daily" | "weekly" | "monthly";

/**
 * ★ Складываем БАЗОВЫЕ величины, а отношения не трогаем вовсе.
 *
 * Читатели выводят TTR, CPT, CR и CPA из базовых столбцов, поэтому
 * сложение расхода и тапов за неделю даёт настоящий недельный CPT само —
 * `расход_за_неделю / тапы_за_неделю`.
 *
 * Усреднить дневные отношения было бы неверно арифметически: неделя, где
 * в понедельник одна установка по $10, а во вторник сто по $1, даёт
 * среднее дневных CPA $5.50 при настоящем $1.09. Разница не в точности —
 * в том, что второе число существует, а первое нет.
 *
 * Цена решения, принятая владельцем: при переключении Daily → Weekly
 * линия объёмных метрик поднимается примерно всемеро и ось
 * перерисовывается. Это не артефакт — за неделю расход и правда всемеро
 * больше дневного.
 */
/** Покрывает ли группа весь свой отрезок — неделю или календарный месяц. */
function coversWholeBucket(
  key: string,
  bucket: readonly EntityChartPoint[],
  period: ChartPeriod,
): boolean {
  const start = new Date(`${key}T00:00:00Z`);
  const end = new Date(start);
  if (period === "weekly") end.setUTCDate(end.getUTCDate() + 6);
  else end.setUTCMonth(end.getUTCMonth() + 1, 0);
  return (
    bucket[0]!.date === key &&
    bucket[bucket.length - 1]!.date === end.toISOString().slice(0, 10)
  );
}

/**
 * Точка, как её принимает `ChartCard`: даты и значения из `DailyPoint`
 * плюс НАСТОЯЩИЙ охват агрегированной группы.
 *
 * ★ Охват обязан ехать вместе с точкой, иначе подписать её нечем.
 */
export type ChartSeriesPoint = DailyPoint & {
  span_from?: string;
  span_to?: string;
  partial?: boolean;
};

/**
 * Проекция агрегированного ряда в то, что читает `ChartCard`.
 *
 * ★★ Вынесено из компонента ИМЕННО ради охвата. Раньше проекция жила
 * выражением внутри `EntityChartPanel` и перечисляла поля вручную —
 * `date`, нули и ничего больше. Значения график берёт не отсюда (они
 * приходят замыканием в `extraMetrics`, §36), поэтому казалось, что
 * кроме даты ничего не нужно. Но подписи — и оси, и подсказки — берут
 * `span_from`/`span_to` ИМЕННО отсюда, и они молча терялись на этой
 * границе: ось писала «24 авг» вместо «24–30 авг», а подсказка над
 * недельной точкой называла один день, хотя значения в ней недельные.
 * Ни одна проверка этого не ловила — поля необязательные, и пропуск
 * законен для компилятора. Поэтому функция чистая и покрыта тестом.
 */
export function toChartSeries(
  points: readonly EntityChartPoint[],
): ChartSeriesPoint[] {
  return points.map((p) => ({
    date: p.date,
    span_from: p.span_from,
    span_to: p.span_to,
    partial: p.partial,
    // Значения графику отсюда не нужны — их даёт `extraMetrics`. Нули
    // здесь только чтобы удовлетворить форму `DailyPoint`.
    spend_usd: "0",
    impressions: 0,
    taps: 0,
    installs: 0,
    trials: 0,
    revenue_usd: null,
  }));
}

export function aggregatePoints(
  points: EntityChartPoint[],
  period: ChartPeriod,
): EntityChartPoint[] {
  if (period === "daily" || points.length === 0) return points;

  const groups = new Map<string, EntityChartPoint[]>();
  for (const p of points) {
    const key = period === "weekly" ? weekStart(p.date) : monthStart(p.date);
    const bucket = groups.get(key);
    if (bucket) bucket.push(p);
    else groups.set(key, [p]);
  }

  return [...groups.entries()]
    .sort(([a], [b]) => (a < b ? -1 : 1))
    .map(([key, bucket]) => {
      // ★ Дата группы — её НАЧАЛО, а не первый день с данными: иначе
      // точки на оси стояли бы неровно, а подпись врала бы о том, какой
      // отрезок она покрывает.
      const out: EntityChartPoint = {
        date: key,
        // ★ Настоящий охват группы. Крайние группы почти всегда неполные:
        // при периоде «1 мая — 28 августа» последняя месячная точка стоит
        // на 1 августа и держит 1–28 августа, а первая недельная — на
        // понедельнике 27 апреля и держит только 1–3 мая. Подпись «1
        // августа» читается как «здесь график кончился», хотя данные до
        // 28-го в ней есть. Ось подписывает край по этому охвату.
        span_from: bucket[0]!.date,
        span_to: bucket[bucket.length - 1]!.date,
        // Неполная группа: покрывает не весь свой отрезок. Решается ЗДЕСЬ,
        // потому что только агрегация знает период; подписывающему коду
        // остаётся прочитать признак.
        partial: !coversWholeBucket(key, bucket, period),
        impressions: 0, taps: 0, spend_usd: 0, installs_asa: 0,
        tap_installs: 0, view_installs: 0, total_installs: 0,
        new_downloads_tap: 0, new_downloads_view: 0, new_downloads_total: 0,
        redownloads_tap: 0, redownloads_view: 0, redownloads_total: 0,
        event_counts: {}, event_revenue: {},
      };
      for (const p of bucket) {
        // ★ Перебираем ИМЕНА СТОЛБЦОВ, а не ключи `READERS`: там
        // идентификаторы метрик, и они с именами столбцов совпадают не
        // все. `installs_total` — метрика, столбец называется
        // `total_installs`; `spend` — метрика, столбец `spend_usd`.
        // Перебор по метрикам молча оставил бы установки нулём на
        // недельном виде, и заметить это было бы нечем: линия просто
        // легла бы на ось.
        for (const c of BASE_SUM_COLUMNS) {
          out[c] += p[c] ?? 0;
        }
        for (const [k, v] of Object.entries(p.event_counts)) {
          out.event_counts[k] = (out.event_counts[k] ?? 0) + v;
        }
        for (const [k, v] of Object.entries(p.event_revenue)) {
          out.event_revenue[k] = (out.event_revenue[k] ?? 0) + v;
        }
      }
      return out;
    });
}

/** Понедельник недели, в которую попадает дата. UTC — как и весь ряд. */
export function weekStart(iso: string): string {
  const d = new Date(`${iso}T00:00:00Z`);
  // getUTCDay(): 0 — воскресенье. Неделя с понедельника, поэтому
  // воскресенье отступает на шесть дней назад, а не на минус один.
  const shift = (d.getUTCDay() + 6) % 7;
  d.setUTCDate(d.getUTCDate() - shift);
  return d.toISOString().slice(0, 10);
}

/** Первое число месяца, в который попадает дата. */
export function monthStart(iso: string): string {
  return `${iso.slice(0, 7)}-01`;
}

/**
 * Какие режимы доступны на выбранном отрезке.
 *
 * ★ Порог — ДВЕ группы, а не одна. Ровно семь дней дают одну недельную
 * точку, и рисовать по ней линию нечего: график из одной точки не
 * показывает динамику, ради которой его открывают.
 */
export function availablePeriods(
  dateFrom: string,
  dateTo: string,
): ChartPeriod[] {
  const days =
    Math.round(
      (Date.parse(`${dateTo}T00:00:00Z`) - Date.parse(`${dateFrom}T00:00:00Z`)) /
        86_400_000,
    ) + 1;
  const out: ChartPeriod[] = ["daily"];
  if (days >= 14) out.push("weekly");
  // Два КАЛЕНДАРНЫХ месяца, а не 60 дней: месячная группировка ходит по
  // календарю, и «два месяца» в днях зависит от того, какие это месяцы.
  if (monthStart(dateFrom) !== monthStart(dateTo)) {
    const a = new Date(`${monthStart(dateFrom)}T00:00:00Z`);
    const b = new Date(`${monthStart(dateTo)}T00:00:00Z`);
    const months =
      (b.getUTCFullYear() - a.getUTCFullYear()) * 12 +
      (b.getUTCMonth() - a.getUTCMonth());
    if (months >= 1) out.push("monthly");
  }
  return out;
}

/**
 * Куда сядет маркер изменения. В дневном режиме день — это точка, и доля
 * равна её индексу. При агрегации день лежит ВНУТРИ точки: берём индекс
 * группы плюс смещение дня внутри неё, чтобы маркер стоял на своём дне,
 * а не на начале недели.
 *
 * Доля от 0 до 1 по ширине области построения; отступы осей прибавляет
 * `ChangeMarkers` — они свойство отрисовки, а не даты.
 */
export function makeMarkerPosition(
  rawPoints: readonly EntityChartPoint[],
  points: readonly EntityChartPoint[],
  period: ChartPeriod,
): (day: string) => number | null {
  const groupIndex = new Map<string, number>();
  points.forEach((p, i) => groupIndex.set(p.date, i));
  const last = points.length - 1 || 1;
  const mark =
    period === "weekly" ? weekStart : period === "monthly" ? monthStart : null;

  const daysInGroup = new Map<string, string[]>();
  if (mark) {
    for (const p of rawPoints) {
      const k = mark(p.date);
      const b = daysInGroup.get(k);
      if (b) b.push(p.date);
      else daysInGroup.set(k, [p.date]);
    }
  }

  return (day: string): number | null => {
    if (!mark) {
      const i = points.findIndex((p) => p.date === day);
      return i < 0 ? null : i / last;
    }
    const key = mark(day);
    const g = groupIndex.get(key);
    if (g == null) return null;
    const bucket = daysInGroup.get(key) ?? [];
    const o = bucket.indexOf(day);
    // Смещение внутри группы — доля её ширины. Одна группа на графике:
    // делить не на что, ставим в начало.
    const within = bucket.length > 1 && o >= 0 ? o / bucket.length : 0;
    return (g + within) / last;
  };
}
